import log, { CLASSIFICATION } from '@mc/es-logger';

let activeObserver = null;
/**
 * @type {Set<string>}
 */
const activeMarks = new Set();
const route = window.location?.pathname;
let navigationStateHasChanged = false;
if (window?.navigation) {
  window.navigation.oncurrententrychange = () => {
    navigationStateHasChanged = true;
  };
}
/**
 * @typedef SmallLCPEntry
 * @description The small LCP entry object
 * @property {string} el - The element
 * @property {number} time - The time at which the LCP occurred
 */

/**
 * @typedef RumMarkerData
 * @description The RUM marker data object
 * @property {number} start - The route start time in ms (utc)
 * @property {number} tcpConnectDt - The TCP connection delta
 * @property {number} dnsLookUpDt - The domain lookup delta
 * @property {number} domContentLoadDt - The DOM content load delta
 * @property {number} domLoadDt - The DOM load delta
 * @property {number} navToFetchDT - The navigation to fetch start delta
 * @property {number} fetchToReqDT - The fetch to request start delta
 * @property {number} responseDt - The response time delta
 * @property {number} unloadEvDt - The unload event time delta
 * @property {number} pgRdy - The Page ready? time (intuit derived metric, we need to confirm what this is)
 */

/**
 * @typedef WebVitalsData
 * @description The web vitals data object
 * @property {string} path - The pathname of the page
 * @property {number} fcp - The first contentful paint
 * @property {SmallLCPEntry[]} lcpEntryArr - The largest contentful paint entries
 * @property {number} lastLcp - The last largest contentful paint
 * @property {RumMarkerData} rumMarkers - The RUM marker data
 * @property {boolean} routeChanged - Whether the route has changed while the observer was active
 * @property {{name: time}} mcMarkerData - Any custom performance markers
 * @property {number} ttfb - The time to first byte (ttfb = responseStart - navigationStart)
 */

/**
 * @typedef UserData
 * @description The user data object
 * @property {number} accountAge - The age of the account
 * @property {string} commercePlan - The commerce plan
 * @property {string} lang - The language
 * @property {number} listSize - The size of the list
 * @property {string} plan - The plan
 * @property {number} viewportWidth - The initial viewport width
 * @property {number} viewportHeight - The initial viewport height
 * @property {string} country - The country
 * @property {string} state - The state
 */

/**
 * @typedef ClientData
 * @description The client data object
 * @property {string} referrer - The referrer
 * @property {number} downLink - The downlink
 * @property {number} rtt - The round trip time
 * @property {number} deviceCores - The number of cores
 * @property {number} deviveMemory - The device memory
 * @property {boolean} isMobile - Whether the device is mobile
 * @property {string} networkType - The effective network type
 * @property {string} platform - The platform
 */

/**
 * @typedef PerformanceData
 * @description The performance data object
 * @property {Object} custom - The custom tags
 * @property {WebVitalsData} vitals - The web vitals data
 * @property {string[]} resources - The cached resources
 * @property {ClientData} clientInfo - The client information
 * @property {UserData} userData - The user data
 */

/**
 * @type {PerformanceData}
 */
window.mcPerformance = {
  custom: {},
  vitals: {
    path: window.location.pathname,
    fcp: 0,
    lcpEntryArr: [],
    lastLcp: 0,
    rumMarkers: {},
    routeChanged: false,
    mcMarkerData: {},
    ttfb: 0,
  },
  resources: [],
  clientInfo: {},
  userData: {},
  distFolderInUse: false,
};

/**
 * @typedef NavigationEntry
 * @property {string} name - The name of the navigation event
 * @property {number} tcp_connect_end - The time at which the TCP connection was established
 * @property {number} response_time - The time it took to receive a response
 * @property {number} dom_interactive - The time at which the DOM became interactive
 * @property {string} navigation_type - The type of navigation event
 * @property {string} referrer - The referrer URL
 */

/**
 * @typedef LCPEntry
 * @property {string} homepage_type - The type of homepage
 * @property {string} onboarding_type - The type of onboarding module
 * @property {string} lcp_element - The element that was painted
 * @property {number} lcp_time - The time at which the LCP occurred
 */

/**
 * @typedef ClientMetrics
 * @property {boolean} main_cached - Whether the main chunk was cached
 * @property {boolean} vendor_cached - Whether the vendor chunk was cached
 * @property {boolean} internal_cached - Whether the internal chunk was cached
 * @property {number} list_size - The size of the list
 * @property {number} account_age - The age of the account
 * @property {string} page_title - The title of the page
 * @property {string} country - The country of the user
 * @property {string} network_effective_type - The effective network type
 */

/**
 * @typedef Metrics
 * @property {ClientMetrics} clientMetrics - Additional metrics
 * @property {NavigationEntry} navigationEntry - The navigation entry
 * @property {LCPEntry} lcpEntry - The LCP entry
 */

/**
 * @type {Metrics}
 */
const metrics = {
  clientMetrics: {},
  navigationEntry: {},
  lcpEntry: {},
};

let logShipped = false;

let lcpTimeoutId = null;
const LCP_TIMEOUT_DURATION = 5000;
const mainAssetChunks = ['main', 'vendor', 'i_n_t_e_r_n_a_l'];

/**
 * @function getPerformanceObserver
 * @param {string} homepageType - The type of homepage
 * @param {string} onboardingType - The type of onboarding module
 *
 * @returns {void}
 */
export function getPerformanceObserver(homepageType, onboardingType) {
  if (activeObserver || logShipped) {
    return;
  }
  const sourcePathPart =
    process.env.NODE_ENV === 'development'
      ? 'http://127.0.0.1:8083/'
      : '.mailchimp.com/release/';
  activeObserver = new PerformanceObserver((list) => {
    const entries = list.getEntries();
    entries.forEach((entry) => {
      if (!entry) {
        return;
      }

      switch (entry.entryType.toLowerCase()) {
        case 'resource':
          for (const chunk of mainAssetChunks) {
            if (entry.name.includes(chunk)) {
              const metricProperty = `${chunk.replace('_', '')}_cached`;
              metrics.clientMetrics[metricProperty] =
                entry.deliveryType === 'cache';
              break;
            }
          }
          if (
            entry.name.includes(sourcePathPart) &&
            entry.name.endsWith('.js')
          ) {
            // Clear the existing timeout if it exists
            if (lcpTimeoutId) {
              clearTimeout(lcpTimeoutId);
            }
            // Set a new timeout
            lcpTimeoutId = setTimeout(() => {
              metrics.clientMetrics.list_size = window.gtmData?.listSize || 0;
              metrics.clientMetrics.account_age =
                window.gtmData?.accountAge || 0;
              metrics.clientMetrics.country =
                window.otStubData?.userLocation?.country || '';
              metrics.clientMetrics.network_effective_type =
                window.O11yRUM?.rumReporter?.tags?.networkEffectiveType || '';
              metrics.navigationEntry.referrer =
                window.navigation?.activation?.from?.url || '';
              if (!logShipped) {
                log({
                  channelName: 'guide-me-performance',
                  message: 'PERFORMANCE_METRICS',
                  data: metrics,
                  classification: CLASSIFICATION.RESTRICTED,
                });
                logShipped = true;
                activeObserver.disconnect();
              }
            }, LCP_TIMEOUT_DURATION);
          }
          break;
        case 'largest-contentful-paint':
          metrics.clientMetrics.page_title = window.document.title;
          /**
           * @type {Element}
           */
          // eslint-disable-next-line no-case-declarations
          const element = entry.element;
          metrics.lcpEntry = {
            homepage_type: homepageType,
            onboarding_type: onboardingType,
            lcp_element: element?.innerHTML?.substring(0, 25) + '...',
            lcp_time: entry.startTime,
          };
          break;
        case 'navigation':
          metrics.navigationEntry = {
            name: entry.name,
            tcp_connect_end: entry.connectEnd,
            response_time: entry.responseEnd - entry.responseStart,
            dom_interactive: entry.domInteractive,
            navigation_type: entry.type,
          };
          break;
        default:
          break;
      }
    });
  });
  activeObserver.observe({ type: 'resource', buffered: true });
  activeObserver.observe({ type: 'largest-contentful-paint', buffered: true });
  activeObserver.observe({ type: 'navigation', buffered: true });
}

const getLocation = () => {
  return {
    country: window.otStubData?.userLocation?.country || '',
    state: window.otStubData?.userLocation?.state || '',
  };
};
/**
 * @function getCustomerData
 * @returns {UserData}
 */
const getCustomerData = () => {
  return {
    accountAge: window.gtmData.accountAge || 0,
    commercePlan: window.gtmData.commercePlan,
    lang: window.gtmData.lang,
    listSize: window.gtmData.listSize || 0,
    plan: window.gtmData.plan,
    viewportWidth: window.gtmData.viewportSize.width,
    viewportHeight: window.gtmData.viewportSize.height,
    ...getLocation(),
  };
};

/**
 * @function getClientInfo
 * @returns {ClientData}
 */
const getClientInfo = () => {
  const {
    referrer = '',
    deviceMemory = 0,
    cores = 0,
    rtt = 0,
    downlink = 0,
    networkDownlink = 0,
    networkEffectiveType = '',
  } = window.O11yRUM?.rumReporter?.tags;

  const clientData = window.clientInformation?.userAgentData;
  return {
    referrer: referrer || window.navigation?.activation?.from?.url || '',
    downLink: downlink || networkDownlink,
    rtt: rtt || 0,
    networkType: networkEffectiveType !== '' ? networkEffectiveType : '4g+',
    deviceMemory: deviceMemory || 0,
    isMobile: clientData?.mobile || false,
    platform: clientData?.platform || '',
    deviceCores: cores || 0,
  };
};

/**
 * @function addRsources
 * @param {PerformanceEntry} entry
 */
function addResources(entry) {
  for (const chunk of mainAssetChunks) {
    if (entry.name.includes(chunk)) {
      const name = chunk.replaceAll('_', '');
      const metricProperty = `${name}Cached`;
      if (
        entry.deliveryType === 'cache' &&
        !window.mcPerformance.resources.includes(metricProperty)
      ) {
        window.mcPerformance.resources.push(metricProperty);
      }
      break;
    }
  }
}

/**
 * @function getRumMarkerData
 * @description Get the computed time deltas for the RUM markers (in ms)
 * @returns {RumMarkerData}
 */
function getRumMarkerData() {
  const rum_data = window.O11yRUM?.rumReporter?.rumInterface?.cache;
  const curRoute = route || window.location.pathname;
  window.mcPerformance.vitals.ttfb =
    rum_data?.marks?.responseStart - rum_data?.marks?.navigationStart;
  return {
    start: rum_data?.marks[curRoute],
    tcpConnectDt: rum_data?.marks?.connectEnd - rum_data?.marks?.connectStart,
    dnsLookUpDt:
      rum_data?.marks?.domainLookupEnd - rum_data?.marks?.domainLookupStart,
    domContentLoadDt:
      rum_data?.marks?.domContentLoadedEventEnd -
      rum_data?.marks?.domContentLoadedEventStart,
    domLoadDt:
      rum_data?.marks?.domInteractive - rum_data?.marks?.domLoading < 0
        ? 0
        : rum_data?.marks?.domInteractive - rum_data?.marks?.domLoading,
    navToFetchDT:
      rum_data?.marks?.fetchStart - rum_data?.marks?.navigationStart,
    fetchToReqDT: rum_data?.marks?.requestStart - rum_data?.marks?.fetchStart,
    responseDt: rum_data?.marks?.responseEnd - rum_data?.marks?.responseStart,
    unloadEvDt:
      rum_data?.marks?.unloadEventEnd - rum_data?.marks?.unloadEventStart,
    pgRdy: rum_data?.measures[route]?.derivedmetricmmduration || 0,
  };
}

function logPerformanceData() {
  window.mcPerformance.vitals.path = route || window.location.pathname;
  window.mcPerformance.vitals.lastLcp =
    window.mcPerformance.vitals.lcpEntryArr[
      window.mcPerformance.vitals.lcpEntryArr.length - 1
    ]?.time || 0;
  window.mcPerformance.vitals.rumMarkers = getRumMarkerData();
  window.mcPerformance.vitals.routeChanged = navigationStateHasChanged;
  window.mcPerformance.userData = getCustomerData();
  window.mcPerformance.clientInfo = window.O11yRUM?.rumReporter?.tags
    ? getClientInfo()
    : {};
  window.mcPerformance.distFolderInUse = window.xp_release_dist_resolution;
  window.mcPerformance.vitals.lcpEntryArr = [];

  if (!logShipped) {
    log({
      channelName: 'guide-me-performance',
      message: 'PERFORMANCE_METRICS_V2',
      data: window.mcPerformance,
      classification: CLASSIFICATION.RESTRICTED,
    });
    logShipped = true;
    activeObserver.disconnect();
  }
}

/**
 * @function processLcpEntry
 * @param {LargestContentfulPaint} entry
 */
function processLcpEntry(entry) {
  // if the entry does not have an element, there is no point in continuing
  if (!entry?.element) {
    return;
  }
  /**
   * @type {Element}
   */
  // eslint-disable-next-line no-case-declarations
  const newElement = document.createElement(entry.element.tagName);
  if (entry.element.textContent?.length) {
    newElement.textContent = entry.element.textContent;
  }
  // eslint-disable-next-line no-case-declarations
  let classList = [];
  if (process.env.NODE_ENV === 'development') {
    classList = Array.from(entry.element?.classList)?.filter((c) =>
      c.includes('Main'),
    );
  } else {
    classList = Array.from(entry.element?.classList).filter(
      (c) => !c.includes('root'),
    );
  }
  if (classList?.length) {
    newElement.classList.add(...classList);
  }
  // eslint-disable-next-line no-case-declarations
  const closingBracketIndex = newElement.outerHTML?.indexOf('>');
  if (closingBracketIndex === -1) {
    return;
  }
  // eslint-disable-next-line no-case-declarations
  let outerHTML = newElement?.outerHTML?.substring(0, closingBracketIndex + 1);
  if (entry.element?.getAttribute('src')?.length) {
    const splitSrc = entry.element?.getAttribute('src')?.split('/');
    const src = `/${splitSrc[splitSrc.length - 2]}/${
      splitSrc[splitSrc.length - 1]
    }`;
    outerHTML = newElement?.outerHTML?.replace(
      newElement.tagName?.toLowerCase(),
      `${newElement.tagName?.toLowerCase()} src="${src}"`,
    );
  }
  window.mcPerformance.vitals.lcpEntryArr.push({
    el: outerHTML,
    time: Math.round(entry.startTime),
  });
}

/**
 * @function getPerformanceObserverNew
 *
 * @returns {void}
 */
export function getPerformanceObserverNew() {
  if (activeObserver || logShipped) {
    return;
  }
  const sourcePathPart =
    process.env.NODE_ENV === 'development'
      ? 'http://127.0.0.1:8083/'
      : '.mailchimp.com/release/';
  activeObserver = new PerformanceObserver((list) => {
    const entries = list.getEntries();
    entries.forEach((entry) => {
      if (!entry) {
        return;
      }

      switch (entry.entryType.toLowerCase()) {
        case 'resource':
          addResources(entry);
          if (
            entry.name.includes(sourcePathPart) &&
            entry.name.endsWith('.js')
          ) {
            // Clear the existing timeout if it exists
            if (lcpTimeoutId) {
              clearTimeout(lcpTimeoutId);
            }
            // Set a new timeout
            lcpTimeoutId = setTimeout(() => {
              logPerformanceData();
            }, LCP_TIMEOUT_DURATION);
          }
          break;
        case 'largest-contentful-paint':
          processLcpEntry(entry);
          break;
        case 'paint':
          if (entry.name === 'first-contentful-paint') {
            window.mcPerformance.vitals.fcp = Math.round(entry.startTime);
          }
          break;
        case 'mark':
          if (entry.name.includes('-mc-perf-')) {
            const name = entry.name.substring(8);
            const time = Math.round(entry.startTime);

            window.mcPerformance.vitals.mcMarkerData[name] = time;
          }
          break;
        default:
          break;
      }
    });
  });
  activeObserver.observe({ type: 'resource', buffered: true });
  activeObserver.observe({ type: 'largest-contentful-paint', buffered: true });
  activeObserver.observe({ type: 'paint', buffered: true });
  activeObserver.observe({ type: 'mark', buffered: true });
}

/**
 * @typedef MarkerDetail
 * @description The custom details of a performance marker
 * @property {string?} prefix - The prefix of the performance marker
 * @property {string?} suffix - The suffix of the performance marker
 * @property {any?} details - The extra details of the performance marker (optional)
 */

/**
 * @function setPerformanceMarker
 * @param {string} name - The name of the performance marker
 * @param {MarkerDetail} detail - The details of the performance marker (optional)
 * @note Technically the optional `detail` parameter must be a structured cloneable type
 * @external ref https://developer.mozilla.org/en-US/docs/Web/API/Web_Workers_API/Structured_clone_algorithm#supported_types
 *
 * @returns {void}
 */
export function setPerformanceMarker(
  name,
  detail = {
    suffix: '',
    details: null,
  },
) {
  if (window.performance) {
    const markerName = detail.suffix
      ? `-mc-perf-${name}-${detail.suffix}`
      : `-mc-perf-${name}`;

    if (!activeMarks.has(markerName)) {
      activeMarks.add(markerName);
    }
    window.performance.mark(markerName, {
      detail: detail.details,
    });
  }
}

export function getPerformanceMarkerByName(name) {
  if (window.performance) {
    return window.performance.getEntriesByName(name);
  }
}

/**
 * @function clearPerformanceMarker
 * @param {string} name - The name of the performance marker
 *
 * @returns {void}
 */
export function clearPerformanceMarker(name) {
  if (window.performance) {
    activeMarks.clear();
    window.performance.clearMarks(name);
  }
}

/**
 * @function getMeasureForMarks
 * @param {string} measureName
 * @param {string} startMarkName
 * @param {string} endMarkName
 * @returns {PerformanceMeasure}
 */
export function getMeasureForMarks(measureName, startMarkName, endMarkName) {
  if (window.performance) {
    return window.performance.measure(measureName, startMarkName, endMarkName);
  }
}

// Utility file to export internal functions solely for the purpose of testing.
// These are not part of the public API and should not be used in production code.
export const _testExports = {
  getRumMarkerData,
  getCustomerData,
  logPerformanceData,
  getClientInfo,
  addResources,
  getLocation,
  processLcpEntry,
  setActiveObserver: (observer) => {
    activeObserver = observer;
  },
};
